import { Alert, Button, DatePicker, Modal, Segmented, Space, Spin, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { purchaseService } from '../services/purchaseService';
import { purchaseReceiptService } from '../services/purchaseReceiptService';
import type { PurchaseReceipt } from '../types/purchaseReceipt';
import type { PurchaseReceiptWithItems } from '../types/purchaseReceipt';
import type { Store } from '../types/store';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import {
  buildLegacyPurchaseReceiptCandidates,
  groupLegacyPurchaseReceiptCandidates,
  LegacyPurchaseReceiptCandidate,
  resolveReceiptSupplier,
  toLegacyPurchaseReceiptItems,
} from '../utils/legacyPurchaseReceiptCandidates';

interface LegacyPurchaseReceiptImportModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (receipt: PurchaseReceipt) => Promise<void> | void;
  onUpdated?: () => Promise<void> | void;
  receiptCodes: string[];
  currentUserId: string;
  materials: WarehouseGoodsItem[];
  stores: Store[];
}

const stateLabel: Record<LegacyPurchaseReceiptCandidate['state'], string> = {
  eligible: 'Sẵn sàng',
  already_imported: 'Đã tạo phiếu',
  not_cukcuk_store: 'Không dùng CUKCUK',
  transfer: 'Chuyển kho',
  invalid_number: 'Dữ liệu không hợp lệ',
  missing_material: 'Chưa khớp nguyên liệu',
  ambiguous_material: 'Trùng tên nguyên liệu',
  missing_cukcuk_mapping: 'Thiếu mapping CUKCUK',
  unit_mismatch: 'Sai đơn vị tính',
};

const stateColor: Partial<Record<LegacyPurchaseReceiptCandidate['state'], string>> = {
  eligible: 'green',
  already_imported: 'blue',
  missing_material: 'orange',
  ambiguous_material: 'orange',
  missing_cukcuk_mapping: 'orange',
  unit_mismatch: 'orange',
};

const nextReceiptCode = (date: Date, codes: string[]): string => {
  const prefix = `NH-${dayjs(date).format('YYYYMMDD')}-`;
  const sequence = codes
    .filter((code) => code.startsWith(prefix))
    .map((code) => Number(code.slice(prefix.length)))
    .filter(Number.isFinite)
    .reduce((max, value) => Math.max(max, value), 0) + 1;
  return `${prefix}${String(sequence).padStart(3, '0')}`;
};

const LegacyPurchaseReceiptImportModal = ({
  open,
  onClose,
  onCreated,
  onUpdated,
  receiptCodes,
  currentUserId,
  materials,
  stores,
}: LegacyPurchaseReceiptImportModalProps) => {
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [periodType, setPeriodType] = useState<'month' | 'year'>('month');
  const [candidates, setCandidates] = useState<LegacyPurchaseReceiptCandidate[]>([]);
  const [importedReceiptsByPurchaseId, setImportedReceiptsByPurchaseId] = useState<Map<string, PurchaseReceiptWithItems[]>>(new Map());
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const loadCandidates = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [purchases, importedReceipts] = await Promise.all([
        periodType === 'month'
          ? purchaseService.getPurchaseItemsByMonth(month.toDate())
          : purchaseService.getPurchaseItemsByYear(month.toDate()),
        purchaseReceiptService.getImportedLegacyPurchaseReceipts(),
      ]);
      setImportedReceiptsByPurchaseId(importedReceipts);
      const cukcukStoreIds = new Set(
        stores
          .filter((store) => Boolean(store.cukcukWarehouseCode))
          .map((store) => store.id)
          .filter((storeId): storeId is string => Boolean(storeId)),
      );
      setCandidates(buildLegacyPurchaseReceiptCandidates({
        purchases,
        materials,
        cukcukStoreIds,
        importedPurchaseIds: new Set(importedReceipts.keys()),
      }));
      setSelectedKeys([]);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Không thể tải dữ liệu mua cũ');
    } finally {
      setLoading(false);
    }
  }, [materials, month, open, periodType, stores]);

  useEffect(() => {
    void loadCandidates();
  }, [loadCandidates]);

  const candidateByKey = useMemo(
    () => new Map(candidates.map((candidate) => [candidate.purchase.id || '', candidate])),
    [candidates],
  );
  const selectedCandidates = useMemo(
    () => selectedKeys
      .map((key) => candidateByKey.get(key))
      .filter((candidate): candidate is LegacyPurchaseReceiptCandidate => Boolean(candidate)),
    [candidateByKey, selectedKeys],
  );
  const selectableKeys = useMemo(
    () => candidates
      .filter((candidate) => candidate.state === 'eligible' || candidate.state === 'already_imported')
      .map((candidate) => candidate.purchase.id || ''),
    [candidates],
  );
  const canCreate = selectedCandidates.length > 0;
  const selectedExistingCandidates = useMemo(
    () => selectedCandidates.filter((candidate) => candidate.state === 'already_imported'),
    [selectedCandidates],
  );
  const canUpdate = selectedExistingCandidates.length > 0;

  const exportAllCandidates = () => {
    const worksheet = XLSX.utils.json_to_sheet(candidates.map((candidate) => ({
      Ngày: dayjs(candidate.purchase.date).format('DD/MM/YYYY'),
      'Nguyên liệu cũ': candidate.purchase.itemName,
      'Số lượng': candidate.purchase.quantity,
      'ĐVT cũ': candidate.purchase.unit,
      'Đơn giá': candidate.purchase.unitPrice,
      'Thành tiền': candidate.purchase.total,
      'Trạng thái': stateLabel[candidate.state],
      'Lý do': candidate.reason,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dữ liệu mua cũ');
    XLSX.writeFile(workbook, `Mua_cu_${month.format(periodType === 'month' ? 'MM-YYYY' : 'YYYY')}.xlsx`);
  };

  const createDraft = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const groups = groupLegacyPurchaseReceiptCandidates(selectedCandidates);
      const createdReceipts: PurchaseReceipt[] = [];
      const codes = [...receiptCodes];
      for (const group of groups) {
        const firstPurchase = group[0].purchase;
        const code = nextReceiptCode(firstPurchase.date, codes);
        const result = await purchaseReceiptService.createReceipt({
          code,
          date: firstPurchase.date,
          storeLocationId: firstPurchase.storeLocationId || '',
          supplierId: firstPurchase.supplierId || null,
          supplierName: firstPurchase.supplierSource || null,
          createdBy: currentUserId,
          note: `Tạo từ ${group.length} dòng dữ liệu mua cũ. Cần đối chiếu chứng từ trước khi xuất CUKCUK.`,
          status: 'draft',
          items: toLegacyPurchaseReceiptItems(group),
        });
        codes.push(code);
        createdReceipts.push(result.receipt);
      }
      messageApi.success(`Đã tạo ${createdReceipts.length} phiếu nháp từ dữ liệu mua cũ`);
      onClose();
      await onCreated(createdReceipts[0]);
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : 'Không thể tạo phiếu nháp');
    } finally {
      setCreating(false);
    }
  };

  const requestCreate = () => {
    if (!canCreate) return;
    const receiptCount = groupLegacyPurchaseReceiptCandidates(selectedCandidates).length;
    Modal.confirm({
      title: 'Tạo phiếu nháp từ dữ liệu mua cũ?',
      content: `Bạn đã chọn ${selectedCandidates.length} dòng. Hệ thống tạo ${receiptCount} phiếu nháp, mỗi phiếu gồm các dòng cùng ngày, cùng cơ sở và cùng nhà cung cấp để phù hợp khi nhập CUKCUK.`,
      okText: 'Tạo phiếu nháp',
      cancelText: 'Quay lại',
      onOk: createDraft,
    });
  };

  const updateExistingDrafts = async () => {
    if (!canUpdate) return;
    setUpdating(true);
    try {
      const receiptsToUpdate = new Map<string, PurchaseReceiptWithItems>();
      const skippedCodes: string[] = [];
      selectedExistingCandidates.forEach((candidate) => {
        const sourcePurchaseId = candidate.purchase.id?.trim();
        const matches = sourcePurchaseId ? importedReceiptsByPurchaseId.get(sourcePurchaseId) || [] : [];
        if (matches.length > 1) {
          matches.forEach((match) => {
            if (!skippedCodes.includes(match.receipt.code)) skippedCodes.push(match.receipt.code);
          });
          return;
        }
        if (matches.length === 1 && matches[0].receipt.id) {
          receiptsToUpdate.set(matches[0].receipt.id, matches[0]);
        }
      });

      let updatedCount = 0;
      let unchangedCount = 0;
      for (const details of Array.from(receiptsToUpdate.values())) {
        const supplier = resolveReceiptSupplier(details.items, materials);
        if (!supplier) {
          skippedCodes.push(details.receipt.code);
          continue;
        }
        if (details.receipt.supplierId === supplier.supplierId
          && details.receipt.supplierName === supplier.supplierName) {
          unchangedCount += 1;
          continue;
        }
        await purchaseReceiptService.updateReceipt(details.receipt.id!, supplier);
        updatedCount += 1;
      }

      if (updatedCount) {
        messageApi.success(`Đã cập nhật nhà cung cấp cho ${updatedCount} phiếu cũ`);
        await onUpdated?.();
      }
      if (unchangedCount) {
        messageApi.info(`${unchangedCount} phiếu đã có đúng nhà cung cấp nên không cần ghi lại`);
      }
      if (skippedCodes.length) {
        messageApi.warning(`Không cập nhật ${skippedCodes.length} phiếu vì có nhiều hoặc thiếu nhà cung cấp: ${skippedCodes.join(', ')}`);
      }
      await loadCandidates();
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : 'Không thể cập nhật phiếu cũ');
    } finally {
      setUpdating(false);
    }
  };

  const requestUpdate = () => {
    if (!canUpdate) return;
    Modal.confirm({
      title: 'Cập nhật phiếu cũ?',
      content: `Hệ thống sẽ giữ nguyên mã phiếu và các dòng nguyên liệu, chỉ bổ sung nhà cung cấp cho các phiếu đã tạo nếu xác định được duy nhất một nhà cung cấp.`,
      okText: 'Cập nhật phiếu cũ',
      cancelText: 'Quay lại',
      onOk: updateExistingDrafts,
    });
  };

  const columns: ColumnsType<LegacyPurchaseReceiptCandidate> = [
    { title: 'Ngày', key: 'date', width: 105, render: (_, candidate) => dayjs(candidate.purchase.date).format('DD/MM/YYYY') },
    { title: 'Nguyên liệu cũ', key: 'itemName', render: (_, candidate) => candidate.purchase.itemName },
    { title: 'SL', key: 'quantity', width: 80, align: 'right', render: (_, candidate) => candidate.purchase.quantity },
    { title: 'ĐVT cũ', key: 'unit', width: 92, render: (_, candidate) => candidate.purchase.unit },
    { title: 'Đơn giá', key: 'unitPrice', width: 125, align: 'right', render: (_, candidate) => candidate.purchase.unitPrice.toLocaleString('vi-VN') },
    { title: 'Trạng thái', key: 'state', width: 155, render: (_, candidate) => <Tag color={stateColor[candidate.state]}>{stateLabel[candidate.state]}</Tag> },
    { title: 'Lý do', dataIndex: 'reason', key: 'reason', width: 280 },
  ];

  return (
    <Modal
      title="Tạo phiếu từ dữ liệu mua cũ"
      open={open}
      onCancel={onClose}
      width={1120}
      footer={(
        <Space>
          <Button onClick={onClose}>Hủy</Button>
          <Button onClick={requestUpdate} loading={updating} disabled={!canUpdate || creating}>
            Cập nhật phiếu cũ
          </Button>
          <Button type="primary" onClick={requestCreate} loading={creating} disabled={!canCreate || updating}>
            Tạo phiếu nháp
          </Button>
        </Space>
      )}
    >
      {contextHolder}
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="Không tự ghép phiếu"
          description="Có thể chọn cả tháng hoặc năm. Hệ thống tách phiếu nháp theo ngày, cơ sở và nhà cung cấp. Dòng đã tạo phiếu vẫn có thể chọn lại để kiểm tra hoặc tạo phiếu test; dùng nút Cập nhật phiếu cũ để bổ sung nhà cung cấp mà không tạo bản ghi mới."
        />
        <Space wrap>
          <Typography.Text strong>Phạm vi dữ liệu:</Typography.Text>
          <Segmented
            value={periodType}
            options={[{ label: 'Tháng', value: 'month' }, { label: 'Năm', value: 'year' }]}
            onChange={(value) => setPeriodType(value as 'month' | 'year')}
          />
          <DatePicker
            picker={periodType}
            value={month}
            format={periodType === 'month' ? 'MM/YYYY' : 'YYYY'}
            allowClear={false}
            onChange={(value) => value && setMonth(value.startOf(periodType))}
          />
          <Button onClick={() => void loadCandidates()} loading={loading}>Tải lại</Button>
          <Button onClick={exportAllCandidates} disabled={!candidates.length}>Xuất tất cả để kiểm tra ({candidates.length})</Button>
          <Button onClick={() => setSelectedKeys(selectableKeys)} disabled={!selectableKeys.length}>Chọn tất cả có thể tạo ({selectableKeys.length})</Button>
          <Button onClick={() => setSelectedKeys([])} disabled={!selectedKeys.length}>Bỏ chọn</Button>
        </Space>
        {loadError && <Alert type="error" showIcon message={loadError} />}
        {loading ? <Spin /> : (
          <Table
            rowKey={(candidate) => candidate.purchase.id || [
              candidate.purchase.itemName,
              dayjs(candidate.purchase.date).valueOf(),
              candidate.purchase.unit,
              candidate.purchase.quantity,
              candidate.purchase.unitPrice,
            ].join('-')}
            columns={columns}
            dataSource={candidates}
            size="small"
            pagination={{ pageSize: 12 }}
            scroll={{ x: 1040 }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => {
                const nextKeys = keys.map(String);
                setSelectedKeys(nextKeys);
              },
              getCheckboxProps: (candidate) => ({ disabled: candidate.state !== 'eligible' && candidate.state !== 'already_imported' }),
            }}
          />
        )}
      </Space>
    </Modal>
  );
};

export default LegacyPurchaseReceiptImportModal;
